/**
 * 認証API実サービス（httpOnly Cookie認証方式）
 *
 * @file auth.service.ts
 * @version 1.0.0
 * @author Japan4U Development Team
 * @summary
 * バックエンド認証APIとの実際の通信を行うサービス。
 * /api/v1/auth/* エンドポイントと連携。
 * httpOnly Cookie方式を採用し、localStorage使用を完全廃止。
 */

import {
  Language,
  Currency,
  LoginRequest,
  RegisterRequest,
  AdminLoginRequest,
  SellerLoginRequest,
  LoginResponse,
  AdminLoginResponse,
  SellerLoginResponse,
  SellerLoginOtpRequiredResponse,
  SellerResendOtpRequest,
  SellerResendOtpResponse,
  SellerAuthResponse,
  User,
  AdminUser,
  UserRole,
  MultiLanguageText
} from '@shared/types';
import { safeFetch } from '@shared/utils/api';
import { getCsrfTokenFromCookie, getCsrfToken } from '@shared/utils/csrf';

// APIベースURL
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
const AUTH_BASE_URL = `${API_BASE_URL}/api/v1/seller/auth`;

// バックエンドレスポンス型定義（backend/app/schemas/に対応）
interface BackendTokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
}

interface BackendUserProfileResponse {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  role: string;
  language: string;
  currency?: string;
  country?: string;
  avatar?: string;
  is_active?: boolean;
  email_verified?: boolean;
  status?: string;
  last_login_at?: string;
  registration_date?: string;
  created_at: string;
  updated_at: string;
}

// バックエンド→フロントエンド型変換
const convertBackendUserToFrontend = (backendUser: BackendUserProfileResponse): User => {
  return {
    id: backendUser.id,
    email: backendUser.email,
    firstName: backendUser.first_name,
    lastName: backendUser.last_name,
    role: backendUser.role as UserRole,
    language: backendUser.language as Language,
    currency: backendUser.currency as Currency | undefined,
    country: backendUser.country,
    avatar: backendUser.avatar,
    isActive: backendUser.is_active,
    emailVerified: backendUser.email_verified,
    status: backendUser.status as 'active' | 'suspended' | 'banned' | 'pending_verification' | 'pending_approval' | undefined,
    lastLoginAt: backendUser.last_login_at ? new Date(backendUser.last_login_at) : undefined,
    registrationDate: backendUser.registration_date ? new Date(backendUser.registration_date) : undefined,
    createdAt: new Date(backendUser.created_at),
    updatedAt: new Date(backendUser.updated_at)
  };
};

// HTTPヘッダー生成（同期版：GETメソッド用）
function getHeaders(): HeadersInit;
// HTTPヘッダー生成（非同期版：POST/PUT/DELETEメソッド用、CSRF対応）
function getHeaders(withCsrf: boolean, role: 'buyer' | 'seller' | 'admin'): Promise<HeadersInit>;
function getHeaders(withCsrf = false, role?: 'buyer' | 'seller' | 'admin'): HeadersInit | Promise<HeadersInit> {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  // 認証はhttpOnly Cookieで自動送信されるため、Authorizationヘッダーは不要

  // CSRF対応が必要な場合は非同期で処理
  if (withCsrf) {
    if (!role) {
      throw new Error('[auth.service] getHeaders: role parameter is required when withCsrf is true');
    }
    return (async () => {
      const csrfToken = getCsrfTokenFromCookie();
      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken;
      } else {
        // Cookieにない場合、APIから取得（ロール指定）
        const token = await getCsrfToken(role);
        headers['X-CSRF-Token'] = token;
      }
      return headers;
    })();
  }

  return headers;
}


export const authService = {
  /**
   * Buyerログイン（一般購入者）
   * POST /api/v1/auth/login
   */
  loginBuyer: async (credentials: LoginRequest): Promise<LoginResponse> => {
    const response = await safeFetch<BackendTokenResponse>(`${AUTH_BASE_URL}/login`, {
      method: 'POST',
      headers: await getHeaders(true, "seller"),
      body: JSON.stringify({
        email: credentials.email,
        password: credentials.password
      }),
      errorMessage: 'ログインに失敗しました。メールアドレスとパスワードを確認してください。'
    });

    // トークンはhttpOnly Cookieで保存されるため、localStorageへの保存不要

    // ユーザー情報を取得
    const user = await authService.getCurrentUser();

    return {
      user,
      accessToken: response.access_token, // UIでの表示用（実際の認証には使用しない）
      refreshToken: response.refresh_token,
      expiresIn: 3600
    };
  },

  /**
   * 後方互換性のための汎用ログイン（loginBuyerのエイリアス）
   * @deprecated Use loginBuyer() instead
   */
  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    return authService.loginBuyer(credentials);
  },

  /**
   * Sellerログイン（販売者）
   * POST /api/v1/seller/auth/login
   * 2FA有効時: {requiresTwoFa: true, resendToken: "..."} を返す
   */
  loginSeller: async (credentials: SellerLoginRequest): Promise<SellerLoginResponse | SellerLoginOtpRequiredResponse> => {
    const requestBody: Record<string, string | boolean> = {
      email: credentials.email,
      password: credentials.password,
    };
    if (credentials.remember !== undefined) {
      requestBody['rememberMe'] = credentials.remember;
    }
    if (credentials.twofaToken) {
      requestBody['twofaToken'] = credentials.twofaToken;
    }

    const data = await safeFetch<{
      requiresTwoFa?: boolean;
      resendToken?: string;
      seller?: {
        id: string;
        email: string;
        businessName?: MultiLanguageText;
        isActive: boolean;
        isVerified: boolean;
        status: string;
        createdAt: string;
        twofaEnabled?: boolean;
      };
      access_token?: string;
      token_type?: string;
      role?: string;
    }>(`${API_BASE_URL}/api/v1/seller/auth/login`, {
      method: 'POST',
      headers: await getHeaders(true, "seller"),
      body: JSON.stringify(requestBody),
      errorMessage: 'Sellerログインに失敗しました。'
    });

    if (data.requiresTwoFa === true) {
      if (!data.resendToken) {
        throw new Error('2FA required but resendToken is missing in response');
      }
      return {
        requiresTwoFa: true,
        resendToken: data.resendToken,
      } as SellerLoginOtpRequiredResponse;
    }

    if (!data.seller || !data.access_token) {
      throw new Error('Sellerログインレスポンスに必須フィールドがありません');
    }

    return {
      seller: {
        id: data.seller.id,
        email: data.seller.email,
        businessName: data.seller.businessName,
        status: data.seller.status,
        commissionRate: 0,
        role: UserRole.SELLER,
        twofaEnabled: data.seller.twofaEnabled,
      },
      accessToken: data.access_token,
      refreshToken: '',
      expiresIn: 3600,
    };
  },

  /**
   * Seller OTP検証（2FAありログイン確定）
   * POST /api/v1/seller/auth/login with twofaToken
   */
  verifySellerOtp: async (email: string, password: string, otpCode: string): Promise<SellerLoginResponse> => {
    const result = await authService.loginSeller({ email, password, twofaToken: otpCode });
    if ('requiresTwoFa' in result) {
      throw new Error('Unexpected requires_2fa response after OTP submission');
    }
    return result as SellerLoginResponse;
  },

  /**
   * OTP再送
   * POST /api/v1/seller/auth/2fa/resend
   */
  resendSellerOtp: async (data: SellerResendOtpRequest): Promise<SellerResendOtpResponse> => {
    const result = await safeFetch<{ resendToken: string }>(
      `${API_BASE_URL}/api/v1/seller/auth/2fa/resend`,
      {
        method: 'POST',
        headers: await getHeaders(true, 'seller'),
        body: JSON.stringify({
          email: data.email,
          resendToken: data.resendToken,
        }),
        errorMessage: 'OTP再送に失敗しました。',
      }
    );
    return { resendToken: result.resendToken };
  },

  /**
   * 販売者プロフィール取得
   * GET /api/v1/seller/auth/me
   */
  getSellerMe: async (): Promise<SellerAuthResponse> => {
    const data = await safeFetch<{
      id: string;
      email: string;
      firstName?: string;
      lastName?: string;
      businessName?: MultiLanguageText;
      status: string;
      twofaEnabled: boolean;
    }>(`${AUTH_BASE_URL}/me`, {
      method: 'GET',
      headers: getHeaders(),
      errorMessage: '販売者情報の取得に失敗しました。',
    });
    return {
      id: data.id,
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
      businessName: data.businessName,
      status: data.status,
      twofaEnabled: data.twofaEnabled,
      role: UserRole.SELLER,
    };
  },

  /**
   * パスワード変更
   * POST /api/v1/seller/auth/me/password
   */
  changeSellerOwnPassword: async (data: { currentPassword: string; newPassword: string }): Promise<void> => {
    await safeFetch<void>(`${AUTH_BASE_URL}/me/password`, {
      method: 'POST',
      headers: await getHeaders(true, 'seller'),
      body: JSON.stringify({
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      }),
      errorMessage: 'パスワードの変更に失敗しました。',
    });
  },

  /**
   * プロフィール更新
   * PUT /api/v1/seller/auth/me/profile
   */
  updateSellerProfile: async (data: { firstName: string; lastName: string }): Promise<SellerAuthResponse> => {
    const result = await safeFetch<{
      id: string;
      email: string;
      firstName?: string;
      lastName?: string;
      businessName?: MultiLanguageText;
      status: string;
      twofaEnabled: boolean;
    }>(`${AUTH_BASE_URL}/me/profile`, {
      method: 'PUT',
      headers: await getHeaders(true, 'seller'),
      body: JSON.stringify({
        firstName: data.firstName,
        lastName: data.lastName,
      }),
      errorMessage: 'プロフィールの更新に失敗しました。',
    });
    return {
      id: result.id,
      email: result.email,
      firstName: result.firstName,
      lastName: result.lastName,
      businessName: result.businessName,
      status: result.status,
      twofaEnabled: result.twofaEnabled,
      role: UserRole.SELLER,
    };
  },

  /**
   * メールアドレス変更リクエスト
   * POST /api/v1/seller/auth/me/email-change-request
   */
  requestSellerEmailChange: async (data: { currentPassword: string; newEmail: string }): Promise<{ success: boolean; message: string }> => {
    return await safeFetch<{ success: boolean; message: string }>(`${AUTH_BASE_URL}/me/email-change-request`, {
      method: 'POST',
      headers: await getHeaders(true, 'seller'),
      body: JSON.stringify({
        currentPassword: data.currentPassword,
        newEmail: data.newEmail,
      }),
      errorMessage: 'メールアドレス変更リクエストに失敗しました。',
    });
  },

  /**
   * メールアドレス変更確認
   * POST /api/v1/seller/auth/verify-email-change
   */
  verifySellerEmailChange: async (token: string): Promise<{ success: boolean; message: string }> => {
    return await safeFetch<{ success: boolean; message: string }>(`${AUTH_BASE_URL}/verify-email-change`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ token }),
      errorMessage: 'メールアドレス変更確認に失敗しました。',
    });
  },

  /**
   * 2FA有効化
   * POST /api/v1/seller/auth/me/2fa/enable
   */
  enableSeller2FA: async (): Promise<{ twofaEnabled: boolean }> => {
    const result = await safeFetch<{ twofaEnabled: boolean }>(`${AUTH_BASE_URL}/me/2fa/enable`, {
      method: 'POST',
      headers: await getHeaders(true, 'seller'),
      body: JSON.stringify({}),
      errorMessage: '二段階認証の有効化に失敗しました。',
    });
    return { twofaEnabled: result.twofaEnabled };
  },

  /**
   * 2FA無効化
   * POST /api/v1/seller/auth/me/2fa/disable
   */
  disableSeller2FA: async (data: { password: string }): Promise<{ twofaEnabled: boolean }> => {
    const result = await safeFetch<{ twofaEnabled: boolean }>(`${AUTH_BASE_URL}/me/2fa/disable`, {
      method: 'POST',
      headers: await getHeaders(true, 'seller'),
      body: JSON.stringify({ password: data.password }),
      errorMessage: '二段階認証の無効化に失敗しました。',
    });
    return { twofaEnabled: result.twofaEnabled };
  },

  /**
   * 管理者ログイン
   * POST /api/v1/admin/auth/login
   */
  adminLogin: async (credentials: AdminLoginRequest): Promise<AdminLoginResponse> => {
    const data = await safeFetch<BackendTokenResponse & { requires_2fa?: boolean }>(`${API_BASE_URL}/api/v1/admin/auth/login`, {
      method: 'POST',
      headers: await getHeaders(true, "seller"),
      body: JSON.stringify({
        email: credentials.email,
        password: credentials.password
      }),
      errorMessage: '管理者ログインに失敗しました。'
    });

    // トークンはhttpOnly Cookieで保存されるため、localStorageへの保存不要

    // 管理者情報をAPI経由で取得
    const adminUser = await authService.getCurrentAdminUser();

    return {
      user: adminUser,
      accessToken: data.access_token, // UIでの表示用
      refreshToken: data.refresh_token,
      requiresTwoFactor: data.requires_2fa || false,
      expiresIn: 3600
    };
  },

  /**
   * Buyer登録（一般購入者）
   * POST /api/v1/auth/register
   */
  registerBuyer: async (data: RegisterRequest): Promise<LoginResponse> => {
    // バックエンドはemailとpasswordのみ受け付ける
    await safeFetch<BackendTokenResponse>(`${AUTH_BASE_URL}/register`, {
      method: 'POST',
      headers: await getHeaders(true, "seller"),
      body: JSON.stringify({
        email: data.email,
        password: data.password
      }),
      errorMessage: 'Buyer登録に失敗しました。'
    });

    // 登録後自動ログイン
    return await authService.loginBuyer({
      email: data.email,
      password: data.password
    });
  },

  /**
   * 後方互換性のための汎用登録（registerBuyerのエイリアス）
   * @deprecated Use registerBuyer() instead
   */
  register: async (data: RegisterRequest): Promise<LoginResponse> => {
    return authService.registerBuyer(data);
  },

  /**
   * ログアウト
   * POST /api/v1/auth/logout
   */
  logout: async (): Promise<void> => {
    try {
      await safeFetch<void>(`${AUTH_BASE_URL}/logout`, {
        method: 'POST',
        headers: await getHeaders(true, "seller"),
        errorMessage: 'ログアウト処理でエラーが発生しました。'
      });
    } catch (error) {
      console.warn('ログアウトAPIエラー:', error);
    }
    // httpOnly Cookieはバックエンドで削除されるため、フロントエンドでの削除処理は不要
  },

  /**
   * 現在のユーザー情報取得
   * GET /api/v1/auth/profile
   */
  getCurrentUser: async (signal?: AbortSignal): Promise<User | AdminUser> => {
    const backendUser = await safeFetch<BackendUserProfileResponse>(`${AUTH_BASE_URL}/profile`, {
      method: 'GET',
      headers: getHeaders(),
      errorMessage: 'ユーザー情報の取得に失敗しました。',
      signal,
    });

    return convertBackendUserToFrontend(backendUser);
  },

  /**
   * 現在の管理者情報取得
   * GET /api/v1/admin/auth/profile
   */
  getCurrentAdminUser: async (signal?: AbortSignal): Promise<AdminUser> => {
    const adminData = await safeFetch<BackendUserProfileResponse & { twofa_enabled?: boolean }>(`${API_BASE_URL}/api/v1/admin/auth/profile`, {
      method: 'GET',
      headers: getHeaders(),
      errorMessage: '管理者情報の取得に失敗しました。',
      signal,
    });

    if (adminData.is_active === undefined || adminData.is_active === null) {
      throw new Error("The administrator's active status (is_active) is required but could not be retrieved.");
    }

    return {
      id: adminData.id,
      email: adminData.email,
      firstName: adminData.first_name,
      lastName: adminData.last_name,
      role: UserRole.ADMIN,
      language: 'ja',
      isActive: adminData.is_active,
      createdAt: new Date(adminData.created_at),
      updatedAt: new Date(adminData.updated_at),
      permissions: [],
      twoFactorEnabled: adminData.twofa_enabled !== undefined ? adminData.twofa_enabled : false,
      failedLoginAttempts: 0
    };
  },

  /**
   * 現在の販売者情報取得
   * GET /api/v1/seller/auth/profile
   */
  getCurrentSellerUser: async (signal?: AbortSignal): Promise<User> => {
    const sellerData = await safeFetch<{
      id: string;
      email: string;
      businessName?: { ja?: string; en?: string; ko?: string };
      businessRegistrationNumber?: string;
      isActive: boolean;
      isVerified: boolean;
      status: string;
      createdAt: string;
    }>(`${API_BASE_URL}/api/v1/seller/auth/profile`, {
      method: 'GET',
      headers: getHeaders(),
      errorMessage: '販売者情報の取得に失敗しました。',
      signal,
    });

    const displayName = sellerData.businessName?.ja || sellerData.businessName?.en || sellerData.businessName?.ko;
    if (!displayName) {
      throw new Error('Seller business name is required but could not be retrieved.');
    }

    // Seller情報をUserオブジェクト形式に変換
    return {
      id: sellerData.id,
      email: sellerData.email,
      firstName: displayName,
      lastName: '',
      role: UserRole.SELLER,
      language: 'ja' as Language,
      isActive: sellerData.isActive,
      status: sellerData.status as 'active' | 'suspended' | 'banned' | 'pending_verification' | 'pending_approval' | undefined,
      createdAt: new Date(sellerData.createdAt),
      updatedAt: new Date(sellerData.createdAt)
    };
  },

  /**
   * トークンリフレッシュ
   * POST /api/v1/auth/refresh
   */
  refreshToken: async (): Promise<{ accessToken: string; expiresIn: number }> => {
    // httpOnly Cookieのrefresh_tokenはバックエンドで自動的に送信される
    const tokenData = await safeFetch<BackendTokenResponse>(`${AUTH_BASE_URL}/refresh`, {
      method: 'POST',
      headers: await getHeaders(true, "seller"),
      body: JSON.stringify({}), // リフレッシュトークンはCookieから自動取得
      errorMessage: 'トークンのリフレッシュに失敗しました。再ログインしてください。'
    });

    // 新しいトークンはhttpOnly Cookieで保存されるため、localStorageへの保存不要

    return {
      accessToken: tokenData.access_token, // UIでの表示用
      expiresIn: 3600
    };
  },

  /**
   * パスワードリセット要求
   * POST /api/v1/auth/reset-password
   */
  resetPassword: async (email: string): Promise<void> => {
    await safeFetch<void>(`${AUTH_BASE_URL}/reset-password`, {
      method: 'POST',
      headers: await getHeaders(true, "seller"),
      body: JSON.stringify({ email }),
      errorMessage: 'パスワードリセットの要求に失敗しました。'
    });
  },

  /**
   * 認証状態確認（新規追加）
   * GET /api/v1/auth/me
   */
  checkAuth: async (): Promise<User | AdminUser> => {
    const backendUser = await safeFetch<BackendUserProfileResponse>(`${AUTH_BASE_URL}/me`, {
      method: 'GET',
      headers: getHeaders(),
      errorMessage: '認証状態の確認に失敗しました。'
    });

    return convertBackendUserToFrontend(backendUser);
  }
};
